/**
 * cart-treatments.js
 *
 * Generic Woobles cart treatments layered on top of the custom cart drawer.
 *
 * Owns:
 * - lightweight active-currency context for custom code
 * - "Too fluffin' cute to miss" heading above suggestions
 * - TINMEUP discount-allocation treatment
 * - hidden-product browsing protection
 * - hidden-product / GWP quantity locking
 *
 * Dependency:
 * - assets/_proteus-utils.js must load first and expose:
 *   window.WooblesUtils.observeSelector
 */

(() => {
  'use strict';

  const observeSelector = window.WooblesUtils?.observeSelector || window.observeSelector;

  if (typeof observeSelector !== 'function') {
    console.error('[CartTreatments] observeSelector unavailable. Ensure _proteus-utils.js loads first.');
    return;
  }

  /* ---------------------------------------------
   * Currency context
   * ------------------------------------------- */

  const shopifyCurrency = window.Shopify?.currency;

  window.active_currency =
    shopifyCurrency?.active || (typeof shopifyCurrency === 'string' ? shopifyCurrency : null) || 'USD';

  /* ---------------------------------------------
   * Suggestions heading
   * ------------------------------------------- */

  observeSelector(
    '.gwp-suggestions__list',
    (list) => {
      if (list.closest('.gwp-suggestions')?.querySelector('.gwp-suggestions__title')) return;
      // Title is already rendered in cart-drawer.liquid — nothing to inject
    },
  );

  /* ---------------------------------------------
   * TINMEUP
   * ------------------------------------------- */

  observeSelector(
    '#CartDrawer .discounts__discount',
    (discountEl) => {
      const text = (discountEl.textContent || '').toUpperCase();
      if (text.includes('TINMEUP')) {
        discountEl.classList.add('js-discount-code--TINMEUP');
      }
    },
  );

  /* ---------------------------------------------
   * Hidden-product helpers
   * ------------------------------------------- */

  const getHiddenProducts = () => (Array.isArray(window.hidden_products) ? window.hidden_products : []);

  const getHiddenVariantIds = () =>
    new Set(
      getHiddenProducts()
        .map((product) => String(product?.var_id ?? product?.variant_id ?? product?.variantId ?? ''))
        .filter(Boolean),
    );

  const getHiddenHandles = () =>
    new Set(
      getHiddenProducts()
        .map((product) => String(product?.handle || '').trim())
        .filter(Boolean),
    );

  const getHiddenPreviewFlag = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('view-hidden-products') === 'true') {
        window.sessionStorage.setItem('view-hidden-products', 'true');
      }
      return window.sessionStorage.getItem('view-hidden-products') === 'true';
    } catch (_) {
      return false;
    }
  };

  const isHiddenProductUrl = (href, hiddenHandles) => {
    if (!href || !hiddenHandles.size) return false;
    let url;
    try {
      url = new URL(href, window.location.origin);
    } catch (_) {
      return false;
    }
    for (const handle of hiddenHandles) {
      if (url.pathname === `/products/${handle}` || url.pathname.endsWith(`/products/${handle}`)) {
        return true;
      }
    }
    return false;
  };

  const removeHiddenProductCard = (link) => {
    const removable =
      link.closest('.grid__item') ||
      link.closest('.predictive-search-item') ||
      link.closest('li.list-view-item') ||
      link.closest('.product-card--list');
    if (removable) removable.remove();
  };

  /* ---------------------------------------------
   * Hidden-product browsing protection
   * ------------------------------------------- */

  const protectCurrentHiddenPdp = () => {
    if (getHiddenPreviewFlag()) return;
    const hiddenHandles = getHiddenHandles();
    if (!hiddenHandles.size) return;
    for (const handle of hiddenHandles) {
      if (
        window.location.pathname === `/products/${handle}` ||
        window.location.pathname.endsWith(`/products/${handle}`)
      ) {
        window.location.replace('/');
        return;
      }
    }
  };

  observeSelector('a[href*="/products/"]', (link) => {
    if (getHiddenPreviewFlag()) return;
    const hiddenHandles = getHiddenHandles();
    if (isHiddenProductUrl(link.getAttribute('href'), hiddenHandles)) {
      removeHiddenProductCard(link);
    }
  });

  protectCurrentHiddenPdp();

  /* ---------------------------------------------
   * Hidden-product / GWP quantity protection
   * ------------------------------------------- */

  const getLineItemVariantId = (lineItem) =>
    String(lineItem?.dataset?.variantId ?? lineItem?.getAttribute?.('data-variant-id') ?? '');

  const lockQuantityControls = (lineItem) => {
    if (!lineItem) return;
    lineItem
      .querySelectorAll(
        [
          '.quantity__button',
          'button[name="minus"]',
          'button[name="plus"]',
        ].join(', '),
      )
      .forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('js-hidden-product-qty-control');
      });

    lineItem
      .querySelectorAll(
        [
          '.quantity__input',
          'input[name="updates[]"]',
        ].join(', '),
      )
      .forEach((input) => {
        input.disabled = true;
        input.readOnly = true;
        input.setAttribute('aria-disabled', 'true');
        input.classList.add('js-disabled-qty');
      });
  };

  const protectCartLineItem = (lineItem) => {
    if (getHiddenPreviewFlag()) return;
    const hiddenVariantIds = getHiddenVariantIds();
    if (!hiddenVariantIds.size) return;

    const variantId = getLineItemVariantId(lineItem);
    if (variantId && hiddenVariantIds.has(variantId)) {
      lockQuantityControls(lineItem);
      return;
    }

    const cartItemUrl = lineItem.getAttribute?.('data-cart-item-url') || '';
    if (cartItemUrl && [...hiddenVariantIds].some((id) => cartItemUrl.includes(id))) {
      lockQuantityControls(lineItem);
    }
  };

  // ── Swapped from AMP selectors to Dawn cart drawer selectors ──
  observeSelector(
    [
      '#CartDrawer tr.cart-item[data-variant-id]',
      'tr[data-cart-item-url]',
      '[data-cart-item][data-variant-id]',
      '[data-cart-item-key][data-variant-id]',
    ].join(', '),
    protectCartLineItem,
  );

})();
